/**
 * A certificate's public page, from the outside: the address it lives at and
 * the client for `GET /api/certificates/<id>` (worker/certificates.ts).
 *
 * The sibling of lib/person.ts, and built on the same two rules for the same
 * reader. The page is opened by somebody who was sent a link and is checking
 * it, so it cannot lean on anything the app has — no session, no mission list,
 * no virtual machine — and everything on it comes from one public request.
 * And the answer is JSON from a Worker that may be a deploy ahead of or behind
 * this bundle, so it is read field by field rather than trusted.
 *
 * ## The address
 *
 * `/c/<id>`, a path for the reason `/u/<tag>` is one: it ends up on a CV, in a
 * message, read aloud down a phone. The course links to exactly this shape, so
 * `certificatePath` is the one place it is written down.
 */
import { CERTIFICATE_ID_PATTERN, certificateDefinition, type CertificateId } from '@engineer-os/engine';
import { chatUrl } from './chat';

/** One checkpoint behind a certificate, as the record froze it at issue. */
export interface CertificateCheckpoint {
  missionId: string;
  /** The grading contract that pass was accepted under, e.g. `pilot-28`. */
  version: string;
  /** Unix milliseconds, the server's clock. */
  verifiedAt: number;
}

/**
 * One issued certificate, as anybody may see it.
 *
 * `valid` and `revokedAt` travel together: a revoked certificate is still a
 * page, because a link somebody already shared must say "no longer valid"
 * rather than "never existed" (docs/certificates.md, "Revocation").
 */
export interface PublicCertificate {
  id: string;
  certificate: CertificateId;
  /** The account name at the moment of issue, frozen with the record. */
  holder: string;
  /** The holder's public page, or null when the answer carried no usable tag. */
  tag: string | null;
  issuedAt: number;
  valid: boolean;
  /** When it was withdrawn; null while it is valid, and possibly null on a record from an older Worker. */
  revokedAt: number | null;
  evidence: CertificateCheckpoint[];
}

export function certificatePath(id: string): string {
  return `/c/${id}`;
}

/**
 * Which certificate this URL names, or null when it is not a certificate page.
 *
 * Upper-cased before it is checked, because an id that has been retyped off a
 * printed page or through a phone's autocorrect arrives in whatever case the
 * keyboard chose, and the Worker upper-cases it too. A trailing slash is
 * accepted for lib/person.ts's reason: chat clients and CMSs add one.
 *
 * Anything that is not the shape of an id is null, which is App — the same
 * trade `/u/` makes. A malformed id cannot be anybody's certificate, and the
 * Worker would answer it not found without a lookup.
 */
export function certificateFromPath(pathname: string): string | null {
  const match = /^\/c\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;
  let id: string;
  try {
    id = decodeURIComponent(match[1]!).toUpperCase();
  } catch {
    return null;
  }
  return CERTIFICATE_ID_PATTERN.test(id) ? id : null;
}

/**
 * The record, or null when there is nothing to show.
 *
 * Null for every negative outcome — an id nobody was issued, an account that
 * was deleted, a Worker that has never heard of the route, no network —
 * because the page says the same thing for all of them. The Worker already
 * refuses to tell the first two apart (a deleted person must not go on being
 * named here), and a page that could tell the rest apart would only be
 * guessing which one a stranger hit.
 */
export async function fetchCertificate(id: string): Promise<PublicCertificate | null> {
  if (!CERTIFICATE_ID_PATTERN.test(id)) return null;
  try {
    const r = await fetch(chatUrl(`/api/certificates/${id}`).href, { cache: 'no-store' });
    if (!r.ok) return null;
    return readCertificate(await r.json());
  } catch {
    return null;
  }
}

const stamp = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.trunc(v) : null);

const TAG = /^[0-9a-f]{32}$/;

/**
 * Exported for the tests, and only for them, for lib/person.ts's reason:
 * `chatUrl` reads `import.meta.env`, which a plain Node test runner leaves
 * undefined. Every decision about what the page will believe is in here.
 */
export function readCertificate(body: unknown): PublicCertificate | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;

  // The four things the page is headed with. Without any one of them there is
  // no honest page to draw: a certificate with no name, nobody's name on it,
  // no date or an id that does not read back as the one in the address.
  if (typeof b.id !== 'string' || !CERTIFICATE_ID_PATTERN.test(b.id)) return null;
  if (typeof b.certificate !== 'string' || !certificateDefinition(b.certificate)) return null;
  if (typeof b.holder !== 'string' || b.holder.trim() === '') return null;
  const issuedAt = stamp(b.issuedAt);
  if (issuedAt === null) return null;

  // Valid only when the Worker says so in exactly those words. A field that
  // is missing or mangled is read as not valid, because the mistake in that
  // direction costs a holder a confusing page and the other one has this
  // domain vouching for a certificate it withdrew.
  const valid = b.valid === true;

  const evidence: CertificateCheckpoint[] = [];
  if (Array.isArray(b.evidence)) {
    for (const raw of b.evidence.slice(0, 64)) {
      if (typeof raw !== 'object' || raw === null) continue;
      const e = raw as Record<string, unknown>;
      const verifiedAt = stamp(e.verifiedAt);
      if (typeof e.missionId !== 'string' || e.missionId === '' || verifiedAt === null) continue;
      evidence.push({ missionId: e.missionId, version: typeof e.version === 'string' ? e.version : '', verifiedAt });
    }
  }

  return {
    id: b.id,
    certificate: b.certificate as CertificateId,
    holder: b.holder,
    tag: typeof b.tag === 'string' && TAG.test(b.tag) ? b.tag : null,
    issuedAt,
    valid,
    revokedAt: valid ? null : stamp(b.revokedAt),
    evidence,
  };
}
